/**
 * useFolderDownload · 文件夹级下载控制器（命名弹窗 + 发起导出）
 *
 * AI 分类列表页（多文件夹批量 / 单条）与详情页（当前文件夹）共用：
 *  1. open(folderIDs) · 打开命名弹窗 + 异步拉取多文件夹合并去重后的命名候选
 *  2. confirm(namingRule, timeParams) · 携命名规则发起 exportFolderAndPhotos
 *
 * 副作用归属：埋点由调用方在 open 前自行上报；下载成功后的页面收尾（如批量退出
 * 多选）通过 onStarted 回调外推，本 hook 只负责数据请求 + 导出 + 弹下载记录抽屉。
 */
import { useCallback, useState } from 'react'
import { message } from 'antd'
import { useAppDispatch } from '../../../app/hooks'
import { fetchFolderPhotoTitles } from '../../../actions/folder'
import {
  exportFolderAndPhotos,
  fetchExportTasksV3,
  setExportListDrawerVisible
} from '../../../actions/photo'
import * as SentryUtils from '../../../utils/sentryUtils'
import type { FolderPhotoTitle } from '../components/DownloadModals'

export interface UseFolderDownloadOptions {
  /** 团队 UUID */
  groupID: string
  /** 当前用户 user_id · 拉水印标题（fetchFolderPhotoTitles）必传 */
  userID: string
  /**
   * 下载发起成功后的收尾回调 · 入参为本次下载的 folderIDs 与是否批量
   * 例如列表页批量下载成功后退出多选模式（详情页单文件夹下载无需收尾，可不传）
   */
  onStarted?: (folderIDs: number[], isBatch: boolean) => void
}

export interface FolderDownloadController {
  /** 命名弹窗是否打开 */
  visible: boolean
  /** 命名规则候选（多文件夹已按 titleName 合并去重） */
  titleList: FolderPhotoTitle[]
  /** 本次是否批量下载 · 仅驱动弹窗内埋点与成功文案 */
  isBatch: boolean
  /** 本次下载目标 folderIDs · 弹窗内埋点用 */
  folderIDs: number[]
  /** 下载请求进行中 · 驱动按钮 loading / disabled */
  downloading: boolean
  /** 打开命名弹窗 + 异步拉取命名候选 */
  open: (folderIDs: number[], isBatch: boolean) => void
  /** 关闭命名弹窗 */
  close: () => void
  /** 弹窗确认 · 携命名规则 + 毫秒级时间范围发起下载 */
  confirm: (
    namingRule: string[],
    timeParams?: { startTime?: number; endTime?: number }
  ) => void
}

interface FolderDownloadTarget {
  folderIDs: number[]
  isBatch: boolean
}

/**
 * @param options - 配置参数，含义见 UseFolderDownloadOptions
 * @returns 文件夹下载弹窗的状态与操作方法，见 FolderDownloadController
 */
export function useFolderDownload({
  groupID,
  userID,
  onStarted
}: UseFolderDownloadOptions): FolderDownloadController {
  const dispatch = useAppDispatch()
  const [target, setTarget] = useState<FolderDownloadTarget | null>(null)
  const [titleList, setTitleList] = useState<FolderPhotoTitle[]>([])
  const [downloading, setDownloading] = useState(false)

  /**
   * 拉取多个文件夹的命名候选并按 titleName 合并去重（同名 titleId 用逗号拼接）。
   * 某个文件夹拉取失败不影响整体，整体异常返回空数组（弹窗仍可用预设命名项）。
   */
  const fetchTitles = useCallback(
    async (folderIDs: number[]): Promise<FolderPhotoTitle[]> => {
      if (!groupID) return []
      try {
        const results = await Promise.all(
          folderIDs.map((folderID) =>
            Promise.resolve(
              dispatch(
                fetchFolderPhotoTitles({
                  userID,
                  groupID,
                  album: { type: 4, id: folderID, filter: '' }
                })
              )
            )
          )
        )
        const allTitles = results.reduce<FolderPhotoTitle[]>((acc, result) => {
          const titles = (result as { titles?: FolderPhotoTitle[] } | undefined)
            ?.titles
          return titles ? [...acc, ...titles] : acc
        }, [])
        return allTitles.reduce<FolderPhotoTitle[]>((acc, title) => {
          const existing = acc.find((t) => t.titleName === title.titleName)
          if (existing) {
            existing.titleId = `${existing.titleId},${title.titleId}`
          } else {
            acc.push({ ...title })
          }
          return acc
        }, [])
      } catch (error) {
        SentryUtils.capturePromiseException(error)
        return []
      }
    },
    [groupID, userID, dispatch]
  )

  const open = useCallback(
    (folderIDs: number[], isBatch: boolean) => {
      if (folderIDs.length === 0 || !groupID) return
      setTitleList([])
      setTarget({ folderIDs, isBatch })
      fetchTitles(folderIDs).then(setTitleList)
    },
    [groupID, fetchTitles]
  )

  const close = useCallback(() => setTarget(null), [])

  const confirm = useCallback(
    (
      namingRule: string[],
      timeParams: { startTime?: number; endTime?: number } = {}
    ) => {
      if (!target || !groupID) return
      const { folderIDs, isBatch } = target
      setTarget(null)
      setDownloading(true)
      Promise.resolve(
        dispatch(
          exportFolderAndPhotos({
            groupID,
            folderIDs,
            namePolicy: { file: namingRule },
            startTime: timeParams.startTime
              ? Math.floor(timeParams.startTime / 1000)
              : 0,
            endTime: timeParams.endTime
              ? Math.floor(timeParams.endTime / 1000)
              : 0
          })
        )
      )
        .then((result: { status?: number } | undefined) => {
          if (!result) {
            message.error('下载请求失败，请稍后重试')
            return
          }
          if (result.status === 0) {
            message.success(
              isBatch
                ? `已发起 ${folderIDs.length} 个文件夹的下载`
                : '下载已发起'
            )
            dispatch(fetchExportTasksV3())
            dispatch(setExportListDrawerVisible(true))
            if (onStarted) onStarted(folderIDs, isBatch)
            return
          }
          // 业务拒绝：复用与删除接口同款 -10 / -56 / -203 文案
          const tip = (() => {
            switch (result.status) {
              case -10:
                return '你不是管理员，无法下载文件夹'
              case -56:
              case -203:
                return '你没有下载权限，请联系团队管理员'
              default:
                return `下载失败（status=${result.status}）`
            }
          })()
          message.error(tip)
        })
        .catch(SentryUtils.capturePromiseException)
        .finally(() => {
          setDownloading(false)
        })
    },
    [target, groupID, dispatch, onStarted]
  )

  return {
    visible: !!target,
    titleList,
    isBatch: target?.isBatch ?? false,
    folderIDs: target?.folderIDs ?? [],
    downloading,
    open,
    close,
    confirm
  }
}
